import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const AnnouncementBanner = ({ announcement, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    // Extract valid messages
    const rawMessages = Array.isArray(announcement?.messages) && announcement.messages.length > 0
        ? announcement.messages
        : [announcement?.text].filter(Boolean);

    const validMessages = rawMessages
        .map(m => (typeof m === 'string' ? m.trim() : m?.text?.trim()))
        .filter(Boolean);

    const isCarousel = Boolean(announcement?.isCarousel && validMessages.length > 1);
    const intervalMs = Math.max(2500, Number(announcement?.slideDuration) || 4000);

    // Auto-advance carousel
    useEffect(() => {
        if (!isCarousel || isPaused || validMessages.length <= 1) return;

        const timer = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % validMessages.length);
        }, intervalMs);

        return () => clearInterval(timer);
    }, [isCarousel, isPaused, validMessages.length, intervalMs]);

    // Handle index bounds if messages change
    useEffect(() => {
        if (currentIndex >= validMessages.length) {
            setCurrentIndex(0);
        }
    }, [validMessages.length, currentIndex]);

    if (!announcement?.isActive || validMessages.length === 0) {
        return null;
    }

    const handlePrev = (e) => {
        e.stopPropagation();
        setCurrentIndex(prev => (prev === 0 ? validMessages.length - 1 : prev - 1));
    };

    const handleNext = (e) => {
        e.stopPropagation();
        setCurrentIndex(prev => (prev + 1) % validMessages.length);
    };

    const currentMessage = validMessages[currentIndex] || validMessages[0];

    return (
        <div
            style={{ backgroundColor: announcement.color || '#b08d57' }}
            className="text-white relative shadow-sm overflow-hidden select-none z-20"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            <div className="container mx-auto px-10 sm:px-14 py-2.5 sm:py-3 flex items-center justify-center min-h-[42px]">
                {/* Carousel Left Arrow */}
                {isCarousel && (
                    <button
                        type="button"
                        onClick={handlePrev}
                        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-black/15 transition-colors text-white/90 hover:text-white"
                        aria-label="Previous announcement"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                )}

                {/* Announcement Content */}
                <div className="flex-1 text-center relative h-6 flex items-center justify-center overflow-hidden">
                    {isCarousel ? (
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentIndex}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.28, ease: 'easeOut' }}
                                className="text-xs sm:text-sm md:text-base font-medium tracking-wide truncate max-w-full px-2"
                            >
                                <span>{currentMessage}</span>
                            </motion.div>
                        </AnimatePresence>
                    ) : (
                        <div className="text-xs sm:text-sm md:text-base font-medium tracking-wide truncate max-w-full px-2">
                            <span>{currentMessage}</span>
                        </div>
                    )}
                </div>

                {/* Carousel Right Arrow */}
                {isCarousel && (
                    <button
                        type="button"
                        onClick={handleNext}
                        className="absolute right-10 sm:right-12 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-black/15 transition-colors text-white/90 hover:text-white"
                        aria-label="Next announcement"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                )}

                {/* Close Button */}
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-white/80 hover:text-white hover:bg-black/15 transition-colors text-xl leading-none w-7 h-7 flex items-center justify-center"
                        aria-label="Close banner"
                    >
                        &times;
                    </button>
                )}
            </div>

            {/* Optional Slide Dots for Carousel */}
            {isCarousel && validMessages.length > 1 && (
                <div className="flex justify-center items-center gap-1.5 pb-1 -mt-1">
                    {validMessages.map((_, idx) => (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => setCurrentIndex(idx)}
                            aria-label={`Go to slide ${idx + 1}`}
                            className={`h-1 rounded-full transition-all duration-300 ${
                                currentIndex === idx
                                    ? 'w-4 bg-white'
                                    : 'w-1.5 bg-white/40 hover:bg-white/70'
                            }`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default AnnouncementBanner;
